<template>
	<div class="subview-block">
		<subview-navigation :tabs="tabs" :layout="layout" :block-id="blockId" v-model="active" @subview-move="subviewMove"></subview-navigation>
		<div class="subview-block-body" @drop="drop" @dragover="dragOver" @dragenter="dragOver" @dragleave="dragging = false" :class="{ 'drag-subview-after' : dragging }">
			<template v-if="loadable">
				<div class="subview-block-inner" v-if="loadable" v-html="loadedHtml"></div>
			</template>
			<div class="subview-block-inner" v-else-if="componentBased"><component :is="resolveSubviewComponent(activeTab.component ?? activeTab.name)" v-bind="componentBinding" :component-name="activeTab.component ?? activeTab.name"></component></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {ref, watch, computed, nextTick} from "vue";
import SubviewNavigation from "@/ui/subview/SubviewNavigation.vue";
import {SubviewTab} from "@t/ui/SubView.ts";
import {NewBlockEvent, SubviewPosition} from "@/ui/subview/EventTypes.ts";
import {calcHorizontalPosition, getDataTransfer, ParsedType, parseObjectRef} from "@/utils/DragDropUtils.ts";
import {injectEnv} from "@fd/Environment.ts";
import resolveSubviewComponent from "@/router/SubviewResolver.ts";
import {SourceType, SrcType} from "@t/meta/SourceType.ts";

const props = withDefaults(defineProps<{
	/** Name of the layout */
	layout: string;
	/** Unique ID of the block */
	blockId: number;
	/** Tabs in the block*/
	tabs:SubviewTab[],
	buildUrl?: (tab:SubviewTab) => string;
}>(), {
});
const emit = defineEmits(['subview-move', 'new-block']);
const env = injectEnv();

const active = ref<string>(defaultTabName());
const dragging = ref<boolean>(false);
const activeTab = computed<SubviewTab|null>(() => {
	for (let t of props.tabs) {
		if (t.name === active.value) return t;
	}
	return null;
});
type LoadingState = "initial" | "loading" | "loaded" | "notfound" | "forbidden" | "error";
const loadedHtml = ref<string>("");
const loadState = ref<LoadingState>("initial");

/** Return true if the content of this subview is loaded from the server */
const loadable = computed<boolean>(() => {
	const at = activeTab.value;
	return at == null ? false : !!at.src;
});

const componentBased = computed<boolean>(() => {
	const at = activeTab.value;
	return at == null ? false : !!at.component;
});

const componentBinding = computed(() => {
	return activeTab.value?.bind ?? {};
});

watch(activeTab, (at) => {
	if (loadable.value) {
		loadHtml();
	} else {
		loadedHtml.value = ""
	}
}, { immediate: true });


interface RemoteBlock {
	html: string          // markup without script tags
	inline: string[]      // inline script code in order
	external: string[]    // src URLs in order
}

function splitHtml(raw: string): RemoteBlock {
	const doc = new DOMParser().parseFromString(raw, 'text/html')
	const inline: string[] = []
	const external: string[] = []

	doc.querySelectorAll('script').forEach(s => {
		if (s.src) external.push(s.src)
		else inline.push(s.textContent ?? '')
		s.remove()                         // strip it from the markup
	})

	return {
		html: doc.body.innerHTML,
		inline,
		external
	}
}

async function executeScripts(block: RemoteBlock) {
	// 1. external <script src="…">, sequentially
	for (const src of block.external) {
		await new Promise((resolve, reject) => {
			const el = document.createElement('script')
			el.src = src
			el.async = false                 // preserve order
			el.onload = resolve
			el.onerror = reject
			document.head.appendChild(el)
		})
	}

	// 2. inline code, sequentially
	for (const code of block.inline) {
		// eslint-disable-next-line no-new-func
		new Function(code)();          // runs in global scope
	}
}


async function loadHtml() {
	let ep = typeof props.buildUrl === "function" ? props.buildUrl(activeTab.value) : activeTab.value.src;
	ep = env.net.urlParam(ep, "tab", activeTab.value.name);
	loadState.value = "loading";
	const resp = await env.net.fetch(ep);
	if (resp.ok) {
		const rawHtml = await resp.text();
		const block = splitHtml(rawHtml);
		loadedHtml.value = block.html;
		loadState.value = "loaded";		// emit to DOM
		nextTick(() => executeScripts(block));	// wait until DOM is updated and execute scripts
	} else {
		loadState.value = "error";
	}
}


function storedActive():string {
	const at = sessionStorage.getItem("activeTabs_" + props.layout);
	if (at == null) return null;
	const atParsed:string[] = JSON.parse(at);
	for (let t of props.tabs) {
		if (atParsed.indexOf(t.name) >= 0) return t.name;
	}
	return null;
}

function addToActiveList(list:string[], activated:string):void {
	if (list.indexOf(activated) >= 0) return;	// already there
	list.push(activated);
}

function removeFromActiveList(list:string[], deactivated:string):void {
	const idx = list.indexOf(deactivated);
	if (idx >= 0) {
		list.splice(idx, 1);
	}
}

watch(active, (activated, deactivated) => {
	const at = sessionStorage.getItem("activeTabs_" + props.layout);
	let list;
	if (at == null) list = [];
	else list = JSON.parse(at);
	removeFromActiveList(list, deactivated);
	addToActiveList(list, activated);
	sessionStorage.setItem("activeTabs_" + props.layout, JSON.stringify(list));
});

function defaultTabName() {
	const stored = storedActive();
	if (stored) return stored;
	return props.tabs.length > 0 ? props.tabs[0].name : "";
}

function subviewMove(e: SubviewPosition) {
	emit("subview-move", e);
}

function somethingActive():boolean {
	for (let t of props.tabs) {
		if (t.name === active.value) return true;
	}
	return false;
}

function dragOver(e:DragEvent) {
	// Find data type we can accept
	let pt:ParsedType = getDataTransfer(e.dataTransfer);
	if (pt) {
		e.preventDefault();	// accepting drop
		e.dataTransfer.dropEffect = "move";
		dragging.value = true;
		return;
	}
	dragging.value = false;
}

function drop(e: DragEvent) {
	dragging.value = false;
	e.preventDefault();
	let pt:ParsedType = getDataTransfer(e.dataTransfer);
	if (pt) {
		let oref = parseObjectRef(e.dataTransfer.getData(pt.full));
		if (oref.type === SrcType.SUBVIEW) {
			const subviewId = oref.id;
			emit('new-block', <NewBlockEvent>{ afterBlock: props.blockId, name: subviewId });
		}
	}
}

watch(() => props.tabs, () => {
	if (!somethingActive()) {
		active.value = defaultTabName();
	}
}, { deep: true });

</script>

<style>
.subview-block-body {
	min-height: 32px;
	border-bottom: 2px solid transparent;

	&.drag-subview-after {
		border-bottom: 2px solid var(--selection-bg);
	}
}

@media (width < 550px) {
	.subview-block-body {
		margin-inline: calc(var(--page-gutter) * -1);
		padding-inline: calc(var(--page-gutter));
		width: calc(100% + 2 * var(--page-gutter));
		width: 100dvw;
		max-width: none;
	}
}

</style>